import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Calendar, 
  Ticket, 
  QrCode, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Package,
  CreditCard,
  Users,
  Trophy
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { GAM_TICKET_LOGO } from '@/config/assets';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/tournaments', label: 'Tournaments', icon: Trophy },
  { to: '/admin/fixtures', label: 'Fixtures', icon: Calendar },
  { to: '/admin/events', label: 'Events', icon: Package },
  { to: '/tickets', label: 'Tickets', icon: Ticket },
  { to: '/admin/validate', label: 'Validate', icon: QrCode },
  { to: '/payments', label: 'Payments', icon: CreditCard },
  { to: '/staff', label: 'Staff', icon: Users },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
];

export const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-border hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#0055FF]/20 shadow-sm">
              <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-lg text-gray-900">Gam Ticket</span>
          </div>
        </div>

        <nav className="sidebar-nav px-4 mt-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded mb-1 transition-colors ${
                  isActive
                    ? 'bg-[#0055FF] text-white'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="font-medium text-foreground">{user?.username}</p>
              <p className="text-muted-foreground text-xs">Admin</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-muted-foreground hover:text-destructive"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-border z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#0055FF]/20 shadow-sm">
            <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav className="p-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-4 rounded mb-2 transition-colors ${
                    isActive
                      ? 'bg-[#0055FF] text-white'
                      : 'text-muted-foreground hover:bg-muted'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-4 rounded w-full text-left text-destructive hover:bg-destructive/10 mt-4"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {title && (
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter text-[#020617] mb-6">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
